import type { BillingPeriod, ChargingModel } from '@/api/generated/enums';
import { formatAmount } from '@core/format/money';

import type { PlanPrice } from './api';

/*
 * A plan's list prices as the Packages page edits them (docs/Platform_Billing_Design.md §4.1).
 *
 * The server keeps one price per way of charging and period, so the page offers exactly those
 * slots: a subscription by the month, the quarter or the year, and a licence with its yearly AMC.
 * A slot left blank is a price the plan does not offer. The rules below mirror the server's
 * refusals (`PlatformBillingService.EnsurePriceShape`), so the page never offers a save it refuses.
 */

export interface PriceSlot {
  key: string;
  chargingModel: Exclude<ChargingModel, 'Pilot'>;
  billingPeriod: BillingPeriod;
  label: string;
}

/** The order the page lists them in. An AMC is yearly, so a licence has one slot only. */
export const priceSlots: readonly PriceSlot[] = [
  { key: 'sub-month', chargingModel: 'Subscription', billingPeriod: 'Monthly', label: 'Monthly' },
  {
    key: 'sub-quarter',
    chargingModel: 'Subscription',
    billingPeriod: 'Quarterly',
    label: 'Quarterly',
  },
  { key: 'sub-year', chargingModel: 'Subscription', billingPeriod: 'Annual', label: 'Yearly' },
  {
    key: 'licence',
    chargingModel: 'LicenceWithAmc',
    billingPeriod: 'Annual',
    label: 'Licence + AMC',
  },
];

/** What a recurring amount buys, for a sentence: "₹900 a month". */
export const periodWords: Record<BillingPeriod, string> = {
  Monthly: 'a month',
  Quarterly: 'a quarter',
  Annual: 'a year',
};

/** The two amounts each slot holds, as typed. The licence fee is used by the licence slot only. */
export interface PriceEntry {
  recurring: string;
  oneTime: string;
}

export type PriceDraft = Record<string, PriceEntry>;

const blankEntry: PriceEntry = { recurring: '', oneTime: '' };

const slotOf = (price: Pick<PlanPrice, 'chargingModel' | 'billingPeriod'>) =>
  priceSlots.find(
    (s) => s.chargingModel === price.chargingModel && s.billingPeriod === price.billingPeriod,
  );

/** The page's starting state: every slot, filled from the plan's active prices. */
export function draftFrom(prices: readonly PlanPrice[]): PriceDraft {
  const draft: PriceDraft = Object.fromEntries(priceSlots.map((s) => [s.key, { ...blankEntry }]));

  for (const price of prices) {
    const slot = slotOf(price);
    if (!slot) continue;
    draft[slot.key] = {
      recurring: String(price.recurringAmount),
      oneTime: price.oneTimeAmount === null ? '' : String(price.oneTimeAmount),
    };
  }

  return draft;
}

/** A typed amount, or null when blank; NaN when it is not a number at all. */
const amountOf = (typed: string): number | null => {
  const trimmed = typed.trim();
  return trimmed === '' ? null : Number(trimmed);
};

/**
 * What stops the save, worded for the person typing. Empty means the draft can be sent. A plan with
 * no prices at all is a real answer — it is offered only at a negotiated price — so an all-blank
 * draft has nothing wrong with it.
 */
export function priceProblems(draft: PriceDraft): string[] {
  const problems: string[] = [];

  for (const slot of priceSlots) {
    const entry = draft[slot.key] ?? blankEntry;
    const recurring = amountOf(entry.recurring);
    const oneTime = slot.chargingModel === 'LicenceWithAmc' ? amountOf(entry.oneTime) : null;

    if ([recurring, oneTime].some((n) => n !== null && (Number.isNaN(n) || n <= 0))) {
      problems.push(`${slot.label}: amounts must be more than zero.`);
      continue;
    }

    if (slot.chargingModel === 'LicenceWithAmc' && (recurring === null) !== (oneTime === null)) {
      problems.push(
        recurring === null
          ? 'Licence + AMC: give the yearly AMC as well as the licence fee.'
          : 'Licence + AMC: give the licence fee as well as the yearly AMC.',
      );
    }
  }

  return problems;
}

/** The request body: one row per slot with a price, blank slots left out (which withdraws them). */
export function pricesRequest(draft: PriceDraft): PlanPrice[] {
  return priceSlots.flatMap((slot) => {
    const entry = draft[slot.key] ?? blankEntry;
    const recurring = amountOf(entry.recurring);
    if (recurring === null) return [];

    return [
      {
        chargingModel: slot.chargingModel,
        billingPeriod: slot.billingPeriod,
        recurringAmount: recurring,
        oneTimeAmount: slot.chargingModel === 'LicenceWithAmc' ? amountOf(entry.oneTime) : null,
      },
    ];
  });
}

/** One price as a phrase: "₹900 a month", "₹30,000 licence, then ₹6,000 a year". */
export function describePrice(price: PlanPrice): string {
  const recurring = `₹${formatAmount(price.recurringAmount)} ${periodWords[price.billingPeriod]}`;

  return price.chargingModel === 'LicenceWithAmc' && price.oneTimeAmount !== null
    ? `₹${formatAmount(price.oneTimeAmount)} licence, then ${recurring}`
    : recurring;
}

/** The label a list row leads with: the slot's own name. */
export const priceLabel = (price: PlanPrice) => slotOf(price)?.label ?? price.chargingModel;
